// A single split-flap "flap" tile, styled after an airport departure board.
import React, { useEffect, useRef, useState } from "react";

interface FlipTileProps {
    digit: string;
    color: string;
}

interface HalfDigitContentProps {
    digit: string;
    half: "top" | "bottom";
    color: string;
}

// Fills its container completely and shows only the top or bottom half of the glyph, by
// centering a double-height copy of the digit and clipping it to the container's bounds.
// The inset shadow near the seam-facing edge gives each half a slight crease, like a card fold.
const HalfDigitContent: React.FC<HalfDigitContentProps> = ({ digit, half, color }) => (
    <div
        className="absolute inset-0 overflow-hidden flex items-center justify-center"
        style={{
            backgroundColor: color,
            boxShadow:
                half === "top"
                    ? "inset 0 -0.15em 0.2em -0.1em rgba(0,0,0,0.5)"
                    : "inset 0 0.15em 0.2em -0.1em rgba(0,0,0,0.5)",
        }}
    >
        <div
            className={`absolute inset-x-0 h-[200%] flex items-center justify-center ${
                half === "top" ? "top-0" : "bottom-0"
            }`}
        >
            <div className="font-plot-space-mono font-bold leading-none text-white text-[1.1em]">{digit}</div>
        </div>
    </div>
);

const FlipTile: React.FC<FlipTileProps> = ({ digit, color }) => {
    const [displayDigit, setDisplayDigit] = useState(digit);
    const [previousDigit, setPreviousDigit] = useState(digit);
    const [isFlipping, setIsFlipping] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (digit === displayDigit) {
            return;
        }

        setPreviousDigit(displayDigit);
        setDisplayDigit(digit);
        setIsFlipping(true);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => setIsFlipping(false), 500);
    }, [digit, displayDigit]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return (
        <div
            className="relative w-[1em] h-[1.5em] shrink-0 rounded-md overflow-hidden shadow-[0_0.15em_0.3em_rgba(0,0,0,0.6)]"
            style={{ perspective: "10em" }}
        >
            {/* Bottom half: always the current digit, updates instantly underneath the flap */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 rounded-b-md overflow-hidden">
                <HalfDigitContent digit={displayDigit} half="bottom" color={color} />
            </div>

            {/* Static top-half card, sitting behind the flap, already showing the NEW digit */}
            <div className="absolute inset-x-0 top-0 h-1/2 rounded-t-md overflow-hidden">
                <HalfDigitContent digit={displayDigit} half="top" color={color} />
            </div>

            {/* Rotating flap: hinged at the seam, it swings a full 180deg so it visibly falls down and
                lands flat over the bottom half. Front face shows the OLD digit's top half; the back
                face (only visible past 90deg) is a blank panel standing in for the card's underside as
                it settles into place, revealing the static card above once it's done. */}
            {isFlipping && (
                <div
                    key={`${previousDigit}-${displayDigit}`}
                    className="absolute inset-x-0 top-0 h-1/2 z-10 top-flip"
                    style={{ transformOrigin: "bottom", transformStyle: "preserve-3d" }}
                >
                    <div className="absolute inset-0 overflow-hidden" style={{ backfaceVisibility: "hidden" }}>
                        <HalfDigitContent digit={previousDigit} half="top" color={color} />
                    </div>
                    <div
                        className="absolute inset-0 overflow-hidden"
                        style={{
                            backfaceVisibility: "hidden",
                            transform: "rotateX(180deg)",
                            backgroundColor: color,
                            boxShadow: "inset 0 0.15em 0.2em -0.1em rgba(0,0,0,0.5)",
                        }}
                    />
                </div>
            )}

            <div className="absolute inset-x-0 top-1/2 h-[0.01em] -translate-y-1/2 bg-black z-20" />

            <div
                className="absolute left-0 top-1/2 w-[0.08em] h-[0.08em] -translate-x-1/2 -translate-y-1/2 rounded-full z-20"
                style={{ backgroundColor: color }}
            />
            <div
                className="absolute right-0 top-1/2 w-[0.08em] h-[0.08em] translate-x-1/2 -translate-y-1/2 rounded-full z-20"
                style={{ backgroundColor: color }}
            />
        </div>
    );
};

export default FlipTile;
